/**
 * Collapsible — divulgación progresiva compartida (REQ-UIP-004, ADR-114).
 *
 * Contrato interno:
 *  - `open?` / `defaultOpen?` / `onOpenChange?`: modo controlado y no controlado.
 *  - `trigger`: contenido de la cabecera (texto, icono, badges). El consumidor
 *    NO debe anidar controles interactivos: el disparador ya es un `<button>`.
 *  - `children`: contenido que se oculta de verdad (atributo `hidden`) cuando
 *    la sección está cerrada.
 *  - `disabled?`: bloquea la alternancia manteniendo el estado actual.
 *
 * Accesibilidad: `aria-expanded` + `aria-controls`, foco visible, operable con
 * Enter/Espacio (semántica nativa de `<button>`), icono `aria-hidden`.
 */

import {
  useCallback,
  useId,
  useState,
  type ReactNode,
} from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '../../lib/utils';

export interface CollapsibleProps {
  /** Estado controlado. Si se omite, el componente gestiona su propio estado. */
  open?: boolean;
  /** Estado inicial en modo no controlado (por defecto cerrado). */
  defaultOpen?: boolean;
  /** Se invoca en cada intento de alternancia con el estado solicitado. */
  onOpenChange?: (open: boolean) => void;
  /** Cabecera visible siempre (no anidar controles interactivos). */
  trigger: ReactNode;
  children: ReactNode;
  disabled?: boolean;
  className?: string;
  triggerClassName?: string;
  contentClassName?: string;
  /** Id del contenido; se genera uno estable si no se aporta. */
  contentId?: string;
  testId?: string;
  /** Etiqueta accesible del disparador cuando el `trigger` es solo visual. */
  ariaLabel?: string;
}

export function Collapsible({
  open,
  defaultOpen = false,
  onOpenChange,
  trigger,
  children,
  disabled = false,
  className,
  triggerClassName,
  contentClassName,
  contentId,
  testId,
  ariaLabel,
}: CollapsibleProps) {
  const isControlled = open !== undefined;
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const isOpen = isControlled ? open : uncontrolledOpen;

  const generatedId = useId();
  const triggerId = `${generatedId}-trigger`;
  const panelId = contentId ?? `${generatedId}-content`;

  const toggle = useCallback(() => {
    if (disabled) return;
    const next = !isOpen;
    if (!isControlled) setUncontrolledOpen(next);
    onOpenChange?.(next);
  }, [disabled, isControlled, isOpen, onOpenChange]);

  return (
    <div
      className={cn('w-full', className)}
      data-testid={testId}
      data-state={isOpen ? 'open' : 'closed'}
    >
      <button
        type="button"
        id={triggerId}
        aria-expanded={isOpen}
        aria-controls={panelId}
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={toggle}
        className={cn(
          'flex w-full items-center justify-between gap-3 rounded-md text-left',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
          'disabled:cursor-not-allowed disabled:opacity-60',
          triggerClassName,
        )}
      >
        <span className="min-w-0 flex-1">{trigger}</span>
        <ChevronDown
          aria-hidden="true"
          className={cn(
            'h-4 w-4 shrink-0 text-muted-foreground',
            'motion-safe:transition-transform motion-safe:duration-200',
            'motion-reduce:transition-none',
            isOpen && 'rotate-180',
          )}
        />
      </button>
      <div
        id={panelId}
        role="region"
        aria-labelledby={triggerId}
        hidden={!isOpen}
        className={cn(
          contentClassName,
          isOpen &&
            'motion-safe:animate-in motion-safe:fade-in-0 motion-safe:slide-in-from-top-1 motion-safe:duration-150',
        )}
      >
        {children}
      </div>
    </div>
  );
}
